import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  RefObject,
} from 'react';
import { Control, Controller, Path } from 'react-hook-form';
import useDebounce from 'redux/utils/debounce-hook';
import styled from 'styled-components';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { removeHtmlTags } from '@act/shared/utilities';
import { Typography } from '@mui/material';
import SaveStatusIndicator from 'toolkit/inputs/save-status-indicator';
import { BaseControlledInputProps, ReemaThemeV2 } from '@act/storybook';
import { useTracking } from '../tracking';

const RTEContainer = styled.div<{ $height?: string | number }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  ${({ $height }) =>
    $height &&
    `
    height: ${typeof $height === 'number' ? `${$height}px` : $height};

    /* Force ReactQuill to take full height */
    .quill {
      height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
    }

    .ql-container {
      flex: 1 !important;
      height:  auto !important;

    }

    .ql-editor {
      height: 100% !important;
      min-height: 0 !important;
    }

    /* Ensure the Quill root element takes full height */
    > div:first-child {
      height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
    }
  `}

  .ql-toolbar {
    padding: 8px 0;
    top: 0;
    background: white;
    z-index: 10;
    flex-shrink: 0;
  }

  .ql-editor {
    min-height: 130px;
    word-break: normal;
    overflow-wrap: break-word;
  }

  .ql-container {
    min-height: 135px;
    background-color: ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[100]};
  }

  /* Override min-height when height prop is provided */
  ${({ $height }) =>
    $height &&
    typeof $height === 'number' &&
    `
    .ql-editor {
      min-height: ${$height}px !important;
    }


  `}
`;

const AbsoluteStatusContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 16px;
  z-index: 1000;
`;

const HelperTextContainer = styled.div`
  padding-bottom: 12px;
  padding-top: 3px;
  margin-left: 14px;
`;

const ErrorTextContainer = styled.div`
  padding-top: 3px;
  margin-left: 14px;
`;

const ErrorText = styled(Typography)`
  color: #d32f2f;
`;

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'link',
];

const getModules = (readOnly: boolean) => ({
  toolbar: readOnly
    ? false
    : [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
      ],
});

export interface RichTextEditorProps {
  control: Control<any>;
  defaultValue?: string;
  name: string;
  readOnly?: boolean;
  showCharacterCount?: boolean;
  autoSaveFn?: (debouncedValue: string) => void;
  size?: 'small' | 'large';
  required?: boolean;
  placeholderText?: string;
  height?: string | number;
  trackingName?: string;
  showSaveIndicator?: boolean;
  style?: React.CSSProperties;
}

export interface RichTextEditorRef {
  cancelPendingAutoSave: () => void;
  markAsSaved: (value: string) => void;
  getCurrentValue: () => string;
}

const AUTOSAVE_UPDATE_THRESHOLD = 5;
export type ControlledRichTextProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> &
  RichTextEditorProps & {
    ref?: RefObject<RichTextEditorRef>;
  };

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      control,
      defaultValue,
      name,
      readOnly,
      showCharacterCount,
      autoSaveFn = null,
      required = false,
      placeholderText = 'Add notes here...',
      height,
      trackingName,
      showSaveIndicator = true,
      style,
    },
    ref
  ) => {
    const [status, setStatus] = useState<'IDLE' | 'SAVING' | 'SAVED'>('IDLE');
    const [value, setValue] = useState<string>(defaultValue || '');
    const [isAutoSaving, setIsAutoSaving] = useState(false);

    const lastSavedValueRef = useRef(defaultValue || '');
    const initialValueRef = useRef(defaultValue || '');
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    const debounceResult = useDebounce<string>(value, 1500);
    const debouncedValue = debounceResult[0];
    const cancelDebounce = debounceResult[1];
    const quillModules = useMemo(() => getModules(!!readOnly), [readOnly]);

    const { generateTrackingProps } = useTracking();

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (statusTimerRef.current) {
          clearTimeout(statusTimerRef.current);
        }
      };
    }, []);

    // Expose methods to parent components
    useImperativeHandle(
      ref,
      () => ({
        cancelPendingAutoSave: () => {
          cancelDebounce();
          setIsAutoSaving(false);
          setStatus('IDLE');
          if (statusTimerRef.current) {
            clearTimeout(statusTimerRef.current);
            statusTimerRef.current = null;
          }
        },
        markAsSaved: (value: string) => {
          lastSavedValueRef.current = value;
        },
        getCurrentValue: () => {
          return value;
        },
      }),
      [cancelDebounce, value]
    );

    // Simple auto-save logic with threshold
    const shouldAutoSave = useMemo(() => {
      if (!autoSaveFn || isAutoSaving) {
        return false;
      }

      // Don't save empty content
      if (
        !debouncedValue ||
        debouncedValue === '<p><br></p>' ||
        debouncedValue.trim() === ''
      ) {
        return false;
      }

      // Only save if content has actually changed significantly
      const hasChanged = debouncedValue !== lastSavedValueRef.current;
      const hasSignificantChange =
        Math.abs(debouncedValue.length - initialValueRef.current.length) >=
        AUTOSAVE_UPDATE_THRESHOLD;

      return hasChanged && hasSignificantChange;
    }, [debouncedValue, autoSaveFn, isAutoSaving]);

    const handleAutoSave = useCallback(async () => {
      if (!shouldAutoSave) return;

      try {
        setIsAutoSaving(true);
        if (showSaveIndicator) {
          setStatus('SAVING');
        }

        await autoSaveFn(debouncedValue);
        lastSavedValueRef.current = debouncedValue;

        if (showSaveIndicator) {
          setStatus('SAVED');
          // Clear any existing timer
          if (statusTimerRef.current) {
            clearTimeout(statusTimerRef.current);
          }
          // Set new timer
          statusTimerRef.current = setTimeout(() => {
            setStatus('IDLE');
            statusTimerRef.current = null;
          }, 1750);
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        if (showSaveIndicator) {
          setStatus('IDLE');
        }
      } finally {
        setIsAutoSaving(false);
      }
    }, [shouldAutoSave, debouncedValue, autoSaveFn, showSaveIndicator]);

    // Update value when defaultValue changes
    useEffect(() => {
      if (defaultValue !== undefined) {
        setValue(defaultValue);
        lastSavedValueRef.current = defaultValue;
        initialValueRef.current = defaultValue;
      }
    }, [defaultValue]);

    // Trigger auto-save when conditions are met
    useEffect(() => {
      if (shouldAutoSave) {
        void handleAutoSave();
      }
    }, [shouldAutoSave, handleAutoSave]);

    const characterCount = useMemo(() => {
      if (!showCharacterCount) return 0;
      return removeHtmlTags(value || defaultValue).length;
    }, [value, defaultValue, showCharacterCount]);

    return (
      <>
        <RTEContainer $height={height}>
          {autoSaveFn && showSaveIndicator && (
            <AbsoluteStatusContainer>
              <SaveStatusIndicator status={status} />
            </AbsoluteStatusContainer>
          )}

          <Controller
            name={name}
            defaultValue={defaultValue}
            control={control as Control}
            rules={{ required }}
            render={({
              field: { onChange, value: formValue },
              fieldState: { error },
            }) => {
              useEffect(() => {
                if (formValue !== value) {
                  setValue(formValue || '');
                  // Update refs when form value changes externally
                  lastSavedValueRef.current = formValue || '';
                  initialValueRef.current = formValue || '';
                }
              }, [formValue, value]);

              const handleChange = useCallback(
                (content: string) => {
                  setValue(content);
                  onChange(content);
                },
                [onChange]
              );

              return (
                <>
                  <div
                    style={style}
                    {...(generateTrackingProps && trackingName
                      ? generateTrackingProps(trackingName)
                      : {})}
                  >
                    <ReactQuill
                      value={value}
                      onChange={handleChange}
                      readOnly={readOnly}
                      modules={quillModules}
                      formats={formats}
                      placeholder={placeholderText}
                    />
                  </div>
                  {error && (
                    <ErrorTextContainer>
                      <ErrorText variant="bodySmall">{error.message}</ErrorText>
                    </ErrorTextContainer>
                  )}
                </>
              );
            }}
          />
        </RTEContainer>
        {showCharacterCount && (
          <HelperTextContainer>
            <Typography variant="bodySmall">
              {characterCount} character(s)
            </Typography>
          </HelperTextContainer>
        )}
      </>
    );
  }
);

// set the display name of the component
RichTextEditor.displayName = 'RichTextEditor';
export default RichTextEditor;
